/**
 * Finds the correct function to parse the given file, then executes the callback function
 * @param file File to parse
 * @param callback Function to execute with the result
 */
const fileParser = (file: File, callback): String => {
  const fileType = file.name.split('.').slice(-1)[0]
  const reader = new FileReader()
  reader.readAsText(file)
  reader.onloadend = () => {
    let result = ''
    switch (fileType) {
      case 'mht':
        result = stepParser(reader.result)
        break
      case 'xaml':
        result = UIPathParser(reader.result)
        break
      default:
        throw new Error('Error message in catch')
    }
    callback(result)
  }
}

/**
 * Function to parse an .mht file from Microsoft Step Recorder into a flow
 * @param fileAsString the read file
 */
const stepParser = (fileAsString: String) => {
  const userInputMap = {
    'Mouse Left Click': 'click()',
    'Keyboard Input': 'input("PLEASE FILL IN HERE")',
    'Mouse Right Click': 'rightClick()',
    'Mouse Left Double Click': 'doubleClick()'
  }

  const genPath = (stack: Array) => {
    // Makes sure stack is an array
    if (stack.length > 0) {
      var path = []
      var lastIndex = stack.length - 1
      const LocalizedControlType = stack[lastIndex].getAttribute('@LocalizedControlType')
      var window =
        LocalizedControlType === 'window' || LocalizedControlType === 'pane'
          ? stack[lastIndex].getAttribute('Name') || stack[lastIndex].getAttribute('ClassName')
          : null
      for (var i = stack.length - (window ? 2 : 1); i >= 0; i--) {
        path.push(
          stack[i].getAttribute('Name') ||
            stack[i].getAttribute('ClassName') ||
            stack[i].getAttribute('LocalizedControlType')
        )
      }
      return (window ? `{!${window}}` : '') + path.join('/')
    }
    return ''
  }

  let currPos = 0
  const xmlInput = fileAsString.substring(
    (currPos = fileAsString.indexOf('<Report>')),
    6 + fileAsString.indexOf('</Report>', currPos)
  )
  const domreader = new DOMParser()
  const doc = domreader.parseFromString(xmlInput, 'text/xml')
  const actions = doc.querySelectorAll('EachAction')
  let resultFlows = []
  for (let nmbr = 0; nmbr < actions.length; nmbr++) {
    const UI = actions[nmbr].querySelectorAll('Level')
    let description = actions[nmbr].querySelector('Action')?.innerHTML
    let comment = actions[nmbr].querySelector('Description')?.innerHTML
    const pathCal = genPath(UI)
    resultFlows.push(`//${comment}\nnew Field("${pathCal}").${userInputMap[description]};\n\n`)
  }
  return resultFlows.join('')
}

/**
 * Funtion to parse an .xaml file from UIPath into a flow
 * @param fileAsString the read file
 * @constructor
 */
const UIPathParser = (fileAsString: String) => {
  // Note: new files uses uix tag, and old ones uses ui
  const domreader = new DOMParser()
  const doc = domreader.parseFromString(
    fileAsString
      // Change all ':' into '.'
      .replaceAll(/<ui:/g, '<ui.')
      .replaceAll(/<\/ui:/g, '</ui.')
      .replaceAll(/<uix:/g, '<uix.')
      .replaceAll(/<\/uix:/g, '</uix.'),
    'text/xml'
  )
  let program = doc.querySelector('Sequence')
  // The function to call when all methods variables have been initialised

  const SequenceHandler = (seq: Element, indents: number) => {
    if (seq) {
      var res = []
      let children = seq.children
      for (let i = 0; i < children.length; i++) {
        res.push(TagHandler(children[i], indents + 1))
      }
      return res.join('\n')
    }
  }

  // used for ui apps that don't have a name in the file
  let lastApps = []

  /**
   * Handles the xml tags
   * @param tag Current element
   * @param indents
   * @returns {string}
   * @constructor
   */
  const TagHandler = (tag: Element, indents: number) => {
    if (!tag) return ''
    let res = []
    tag.getAttribute('DisplayName') && res.push(`// ${tag.getAttribute('DisplayName')}`)
    switch (tag.nodeName) {
      case 'Sequence.Variables':
        res.push(VarHandler(tag, indents))
        break
      case 'ui.OpenBrowser':
        const browserType =
          tag.getAttribute('BrowserType') ||
          tag.querySelector('ui\\.OpenBrowser\\.BrowserType').children[0].innerHTML
        const currApp = `app${lastApps.length + 1}`
        lastApps.push(currApp)
        res.push(
          `var ${currApp} = new Application("${browserType}"); // Add Url: ${tag.getAttribute(
            'Url'
          )}`
        )
        res.push(SequenceHandler(tag.querySelector('Sequence'), indents))
        break
      case 'ui.Click':
        var uiClickType = ''
        switch (tag.getAttribute('ClickType')) {
          case 'CLICK_SINGLE':
            uiClickType = 'click()'
            break
          case 'CLICK_DOUBLE':
            uiClickType = 'doubleClick()'
            break
          default:
            uiClickType = `${tag.getAttribute('ClickType')} not supported`
        }
        const path = TagHandler(tag.querySelector('ui\\.Target'), indents)
        res.push(`${path}.${uiClickType}`)
        break
      case 'ui.TypeInto':
        const typeIntoTarget = TagHandler(tag.querySelector('ui\\.Target'), indents)
        res.push(`${typeIntoTarget}.input("${tag.getAttribute('Text')}")`)
        break
      case 'ui.Target':
        const uiAtts = SelectorHandler(tag.getAttribute('Selector'))
        let uiTag = uiAtts.getNamedItem('tag')?.value
        uiTag = uiTag + '/' + uiAtts.getNamedItem('id')?.value
        return `new Field("**/${uiTag}", ${lastApps[lastApps.length - 1]})`
      case 'ui.GetValue':
        res.push(
          `${tag.querySelector('OutArgument')?.innerHTML} = ${TagHandler(
            tag.querySelector('ui\\.Target'),
            indents
          )}.read()`
        )
        break
      case 'ui.MessageBox':
        const message = tag.getAttribute('Text') || ''
        let caption = tag.getAttribute('Caption') || ''
        if (caption === '{x:Null}') caption = '""'
        res.push(`Dialog.info(${caption}, ${message})`)
        break
      /** ALL the uix-format */
      case 'uix.NApplicationCard':
        let TargetApp = tag.children[1].children[0]
        let selecAtts = SelectorHandler(TargetApp.getAttribute('Selector'))
        const app = selecAtts.getNamedItem('app')?.value.split('.')[0]
        res.push(
          `var ${
            app // exclude .exe
          } = new Application({ launchwith: "${findLauncher(
            selecAtts
          )}", titlebarcontains: "${findTitle(selecAtts)}", type: ApplicationTypes.${findType(
            selecAtts
          )} });`
        )
        res.push(SequenceHandler(tag.children[0].querySelector('Sequence'), indents))
        break
      case 'uix.NClick':
        const target = findTargetAnch(tag, indents)
        const button = tag.getAttribute('MouseButton')
        const type = tag.getAttribute('ClickType')
        const clickTypeMap = {
          'Single Left': 'click()',
          'Single Right': 'rightClick()',
          'Double Left': 'doubleClick()'
        }
        const clickType = clickTypeMap[type + ' ' + button]
        res.push(`${target}.${clickType};`)
        break
      case 'uix.NTypeInto':
        const targetType = findTargetAnch(tag, indents)
        res.push(`${targetType}.input("${tag.getAttribute('Text')}")`)
        break
      case 'uix.NGetText':
        const readTarget = findTargetAnch(tag, indents)
        res.push(`${tag.querySelector('OutArgument')?.innerHTML} = ${readTarget}.read()`)
        break
      default:
    }
    res.push('')
    res = res.map(parseFormat)
    const thisIndents = indentCalculator(indents)
    res = res.map(e => thisIndents + e)
    return res.join(`\n`)
  }

  /**
   * Finds all variables
   * @param seqVars the variable element
   * @param indents
   * @returns {string} of all variables
   */
  const VarHandler = (seqVars: Element, indents: number) => {
    let vars = seqVars.children
    let varRes = []
    const typeMapper = (currVar, val) => {
      if (currVar.getAttribute('x:TypeArguments')) {
        switch (currVar.getAttribute('x:TypeArguments').substring(2)) {
          case 'Boolean':
            val = val.toLowerCase() || null
            break
          case 'String':
            if (val.charAt(0) !== '[') val = `"${val}"`
            break
        }
      }
      return val
    }
    for (let i = 0; i < vars.length; i++) {
      let val = ''
      if (vars[i].children.length > 0) {
        // New varDecl
        const lit = vars[i].querySelector('Literal')
        val = lit?.getAttribute('Value') || `${lit.innerHTML}` || `valueNotFound`
        val = typeMapper(vars[i], val)
      } else {
        // Old varDecl
        val = vars[i].getAttribute('Default') || ''
        val = typeMapper(vars[i], val)
      }
      varRes.push(`var ${vars[i].getAttribute('Name')} = ${val};`)
      // varRes.push(`var ${vars[i].getAttribute('Name')}` + (val ? ` = ${val}` : '') + ';')
    }
    const thisIndents = indentCalculator(indents)
    return varRes.join(`\n${thisIndents}`)
  }

  /**
   * Finds all attributes of a xml style attribute.
   * @param selec the attribute
   * @returns {NamedNodeMap} of the attributes
   */
  const SelectorHandler = (selec: String) => {
    let selectorTag = new DOMParser()
      .parseFromString(selec, 'text/xml')
      .querySelector('wnd, html, ctrl, webctrl')
    return selectorTag.attributes
  }
  /**
   * Finds the name attribute in the last tag from a FullSectorAgument
   * @param selec the FullSelectorAgument attribute
   * @returns {name}
   */
  const FullSelectorHandler = (selec: String) => {
    const Tags = selec.replaceAll('>', '>\n').match(/<.*>/gi)
    const findAtts = (unparsedSElem: String) => {
      const thisTag = new DOMParser()
        .parseFromString(unparsedSElem, 'text/xml')
        .querySelector('wnd, ctrl, html, webctrl')
      return (
        thisTag.getAttribute('name') ||
        thisTag.getAttribute('title') ||
        thisTag.getAttribute('cls') ||
        thisTag.getAttribute('aaname') ||
        ''
      )
    }
    for (let i = Tags.length - 1; i >= 0; i--) {
      const thisAtts = findAtts(Tags[i])
      if (thisAtts) return thisAtts
    }
  }
  /**
   * Adds a fitting amount of indents
   */
  const indentCalculator = (indents: number) => {
    return _.repeat(`\t`, indents)
  }

  const findType = (selector: NamedNodeMap): String => {
    switch (findLauncher(selector).split('.')[0]) {
      case 'chrome':
        return 'CHROME'
      case 'msedge':
        return 'EDGE'
      default:
        return 'NATIVE'
    }
  }
  /**
   * Finds the field
   * @param elem the current element, target must be a child to this
   * @param indents the current amount of indents
   * @returns the target field in the current element
   */
  const findTargetAnch = (elem: Element, indents: number) => {
    const targetAnch = elem.querySelector('uix\\.TargetAnchorable')
    if (targetAnch) {
      const app1 = SelectorHandler(targetAnch.getAttribute('ScopeSelectorArgument')).getNamedItem(
        'app'
      )?.value
      const field =
        FullSelectorHandler(targetAnch.getAttribute('FullSelectorArgument')) ||
        FullSelectorHandler(targetAnch.getAttribute('FuzzySelectorArgument'))
      return `new Field("**/${field}", ${app1.split('.')[0]})`
    } else {
      return `new Field("**/TARGET NOT FOUND}", APP_NOT_FOUND)`
    }
  }

  const findTitle = (selector: NamedNodeMap) => {
    return selector.getNamedItem('title')?.value || 'TitleNotFound'
  }

  const findLauncher = (selector: NamedNodeMap): String => {
    return selector.getNamedItem('app')?.value
  }
  /**
   * Transforms UIpath format into flow format, so "[k(enter)]" becomes "<enter>", also removes all "[" and "]"
   * @param input UIPath format
   * @returns Flow Format
   */
  const parseFormat = (input: String) => {
    return input.replaceAll(/(\[k\()(.+)(\)\])/gi, '<$2>').replaceAll(/\[|\]/g, '')
  }
  return SequenceHandler(program, -1)
}

const self = {
  stepParser,
  UIPathParser,
  fileParser
}

export default self
